import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, LogOut, Lock, Video, CheckCircle2, RefreshCw, User, Mail, Phone, RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { adminListConsultationOrders, adminSetConsultationFulfilled } from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const fmtHuDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('hu-HU'); } catch { return iso; }
};

const fmtHuf = (n) => (typeof n === 'number' ? n.toLocaleString('hu-HU') + ' Ft' : '—');

const PayBadge = ({ status }) => {
  const map = {
    succeeded: ['bg-emerald-500/15 text-emerald-300', 'Fizetve'],
    prepared:  ['bg-amber-500/15 text-amber-300', 'Megkezdett'],
    canceled:  ['bg-gray-500/15 text-gray-400', 'Megszakítva'],
    expired:   ['bg-gray-500/15 text-gray-400', 'Lejárt'],
  };
  const [cls, lbl] = map[status] || ['bg-gray-500/15 text-gray-300', status || '—'];
  return (
    <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${cls}`}>
      {lbl}
    </span>
  );
};

const LoginScreen = ({ onAuth }) => {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminListConsultationOrders(key.trim());
      localStorage.setItem(ADMIN_KEY_STORAGE, key.trim());
      onAuth(key.trim());
    } catch (_) { toast.error('Érvénytelen admin kulcs.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <form onSubmit={submit} data-testid="admin-consultations-login" className="w-full max-w-md bg-gray-800/80 rounded-2xl p-8 border border-gray-700 shadow-2xl">
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
            <Video className="text-yellow-500" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>Online konzultációk</h1>
        <Input data-testid="admin-consultations-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="ADMIN_API_KEY" className="bg-gray-900 border-gray-700 text-white mb-4" />
        <Button data-testid="admin-consultations-login-btn" type="submit" disabled={busy || !key.trim()} className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
          {busy ? <><Loader2 className="mr-2 animate-spin" size={16} /> Belépés…</> : <><Lock className="mr-2" size={16} /> Belépés</>}
        </Button>
      </form>
    </div>
  );
};

const AdminConsultationOrdersPage = () => {
  useDocumentMeta({ title: 'Online konzultációk | Admin | Orosz Coaching' });
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('succeeded'); // succeeded | fulfilled | open | all
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const data = await adminListConsultationOrders(adminKey);
      setOrders(data.orders || []);
      setCounts(data.counts || {});
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Lejárt admin kulcs.');
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else {
        toast.error(err?.response?.data?.detail || 'Betöltési hiba.');
      }
    } finally { setLoading(false); }
  }, [adminKey]);

  useEffect(() => { if (adminKey) load(); }, [adminKey, load]);

  const toggleFulfilled = async (order) => {
    setBusy(order.order_id);
    try {
      await adminSetConsultationFulfilled(adminKey, order.order_id, !order.fulfilled);
      toast.success(!order.fulfilled ? 'Teljesítve jelölve.' : 'Visszaállítva.');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Sikertelen művelet.'); }
    finally { setBusy(null); }
  };

  if (!adminKey) return <LoginScreen onAuth={setAdminKey} />;

  const rows = orders.filter((o) => {
    if (filter === 'succeeded') return o.status === 'succeeded' && !o.fulfilled;
    if (filter === 'fulfilled') return !!o.fulfilled;
    if (filter === 'open') return o.status !== 'succeeded';
    return true;
  });

  const filters = [
    { key: 'succeeded', label: `Fizetve, teljesítésre vár (${(counts.succeeded || 0) - (counts.fulfilled || 0)})` },
    { key: 'fulfilled', label: `Teljesítve (${counts.fulfilled || 0})` },
    { key: 'open',      label: `Megkezdett / meghiúsult (${counts.prepared || 0})` },
    { key: 'all',       label: 'Összes' },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <Video className="text-yellow-500" size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Online konzultációk</h1>
              <p className="text-xs text-gray-400">Önálló, egyszeri online konzultáció rendelések (Barion) — teljesítés jelölése.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="admin-consultations-refresh" variant="ghost" size="sm" onClick={load} className="text-gray-300 hover:text-white">
              <RefreshCw size={14} className="mr-1.5" /> Frissítés
            </Button>
            <Button data-testid="admin-consultations-logout" variant="ghost" size="sm" onClick={() => { localStorage.removeItem(ADMIN_KEY_STORAGE); setAdminKey(''); }} className="text-gray-400 hover:text-red-400">
              <LogOut size={14} />
            </Button>
          </div>
        </div>
        <div className="container mx-auto px-6 pb-3 flex flex-wrap gap-1.5">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={`admin-consultations-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={
                'text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ' +
                (filter === f.key
                  ? 'bg-yellow-500 text-gray-900'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700')
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-20"><Loader2 className="animate-spin mx-auto text-yellow-500 mb-2" size={28} /><p className="text-gray-400 text-sm">Betöltés…</p></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-24 text-gray-400" data-testid="admin-consultations-empty">Nincs találat ebben a szűrőben.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((o) => (
              <div key={o.order_id} data-testid={`admin-consultation-row-${o.order_id}`} className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <PayBadge status={o.status} />
                      {o.fulfilled && (
                        <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-sky-500/15 text-sky-300">
                          <CheckCircle2 size={11} className="mr-1" /> Teljesítve
                        </span>
                      )}
                      <span className="text-sm font-bold text-white">{fmtHuf(o.amount_huf)}</span>
                      <span className="text-xs text-gray-500">{fmtHuDate(o.created_at)}</span>
                      <span className="text-[10px] text-gray-500 font-mono">{o.order_id}</span>
                    </div>
                    <div className="text-sm text-gray-300 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span><User size={12} className="inline-block mr-1 -mt-0.5 text-gray-500" />{o.billing?.name || '—'}</span>
                      {o.billing?.email && (
                        <a href={`mailto:${o.billing.email}`} className="text-yellow-500/90 hover:text-yellow-400">
                          <Mail size={12} className="inline-block mr-1 -mt-0.5" />{o.billing.email}
                        </a>
                      )}
                      {o.billing?.phone && (
                        <span className="text-gray-400"><Phone size={12} className="inline-block mr-1 -mt-0.5 text-gray-500" />{o.billing.phone}</span>
                      )}
                    </div>
                    {o.fulfilled && o.fulfilled_at && (
                      <div className="text-xs text-gray-500 mt-1">Teljesítve: {fmtHuDate(o.fulfilled_at)}</div>
                    )}
                  </div>
                  {o.status === 'succeeded' && (
                    <div className="flex items-center gap-2 shrink-0">
                      {o.fulfilled ? (
                        <Button data-testid={`admin-consultation-unfulfill-${o.order_id}`} variant="ghost" size="sm" disabled={busy === o.order_id} onClick={() => toggleFulfilled(o)} className="text-gray-400 hover:text-white">
                          <RotateCcw size={14} className="mr-1" /> Visszaállítás
                        </Button>
                      ) : (
                        <Button data-testid={`admin-consultation-fulfill-${o.order_id}`} size="sm" disabled={busy === o.order_id} onClick={() => toggleFulfilled(o)} className="bg-emerald-600 hover:bg-emerald-700 font-bold">
                          {busy === o.order_id ? <><Loader2 className="animate-spin mr-1.5" size={14} /> …</> : <><CheckCircle2 size={14} className="mr-1" /> Teljesítve</>}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminConsultationOrdersPage;
